import React from 'react';
import { parse, format } from 'date-fns';
import type { MonthlyStats } from './calendarUtils';
import styles from './CalendarStats.module.css';

interface CalendarStatsProps {
  stats: MonthlyStats;
}

function formatPnl(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}$${value.toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  const d = parse(dateStr, 'yyyy-MM-dd', new Date());
  return format(d, 'MMM d');
}

const CalendarStats: React.FC<CalendarStatsProps> = ({ stats }) => {
  const pnlClass = stats.totalPnl >= 0 ? styles.totalPositive : styles.totalNegative;

  return (
    <div>
      <div className={styles.statsBar}>
        <span className={`${styles.totalPnl} ${pnlClass}`}>{formatPnl(stats.totalPnl)}</span>

        <span className={styles.statItem}>
          <span className={styles.statValue}>{stats.tradeCount}</span> trades
        </span>

        <span className={styles.statItem}>
          <span className={styles.statValue}>{stats.winDays}W</span> /{' '}
          <span className={styles.statValue}>{stats.lossDays}L</span> days
        </span>

        {stats.currentStreak.count > 0 && (
          <span className={styles.statItem}>
            <span className={styles.statValue}>
              {stats.currentStreak.count}
              {stats.currentStreak.type === 'win' ? 'W' : 'L'}
            </span>{' '}
            streak
          </span>
        )}

        {stats.longestStreak.count > 0 && (
          <span className={styles.statItem}>
            Best:{' '}
            <span className={styles.statValue}>
              {stats.longestStreak.count}
              {stats.longestStreak.type === 'win' ? 'W' : 'L'}
            </span>
          </span>
        )}
      </div>

      {(stats.bestDay || stats.worstDay) && (
        <div className={styles.highlightsRow}>
          {stats.bestDay && (
            <div className={`${styles.highlightCard} ${styles.highlightBest}`}>
              <span className={styles.highlightLabel}>Best</span>
              <span className={styles.highlightAmount}>{formatPnl(stats.bestDay.netPnl)}</span>
              <span className={styles.highlightLabel}>{formatDate(stats.bestDay.date)}</span>
            </div>
          )}
          {stats.worstDay && (
            <div className={`${styles.highlightCard} ${styles.highlightWorst}`}>
              <span className={styles.highlightLabel}>Worst</span>
              <span className={styles.highlightAmount}>{formatPnl(stats.worstDay.netPnl)}</span>
              <span className={styles.highlightLabel}>{formatDate(stats.worstDay.date)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarStats;
